import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTabData } from '../hooks/useTabData';
import { fetchMedals, fetchOrganisations } from '../services/apiService';
import { SkeletonLoader } from './SkeletonLoader';
import { getCountryName } from '../config/countryNames';

const FLAG_ICON_MODULES = import.meta.glob('../assets/images/icons/national-flag-*.svg', {
  eager: true,
  import: 'default'
});

const DEFAULT_FLAG_MODULE = import.meta.glob('../assets/images/icons/icon-default.svg', {
  eager: true,
  import: 'default'
});

const FLAG_ICONS_BY_CODE = Object.entries(FLAG_ICON_MODULES).reduce((acc, [filePath, assetUrl]) => {
  const match = filePath.match(/national-flag-([a-z0-9-]+)\.svg$/);
  if (!match) return acc;
  acc[match[1].toUpperCase()] = assetUrl;
  return acc;
}, {});

const DEFAULT_FLAG_ICON = Object.values(DEFAULT_FLAG_MODULE)[0] || '';

export const MedalTally = ({ isActive, noHeightLimit = false }) => {
  const { data, isLoading, error, refetch } = useTabData('medals', fetchMedals);
  const [sortBy, setSortBy] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [fallbackRows, setFallbackRows] = useState([]);
  const [isFallbackLoading, setIsFallbackLoading] = useState(false);
  const tableBodyRef = useRef(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const VISIBLE_COUNT = 20;

  const isCountryRecord = (row) => Boolean(row?.OrganisationCode);

  const medalCountryRows = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.filter(isCountryRecord);
  }, [data]);

  const shouldUseFallback = !isLoading && !error && Array.isArray(data) && medalCountryRows.length === 0;

  useEffect(() => {
    if (!shouldUseFallback) {
      setFallbackRows([]);
      setIsFallbackLoading(false);
      return undefined;
    }

    let cancelled = false;

    const buildFallbackRows = async () => {
      try {
        setIsFallbackLoading(true);
        const organisations = await fetchOrganisations();
        if (cancelled) return;

        const uniqueCodes = Array.from(new Set(
          organisations
            .map((org) => (org?.Code || '').toString().trim().toUpperCase())
            .filter(Boolean)
        ));

        const sortedCodes = uniqueCodes.sort((a, b) => {
          const countryCompare = getCountryName(a).localeCompare(getCountryName(b));
          if (countryCompare !== 0) return countryCompare;
          return a.localeCompare(b);
        });

        const rows = sortedCodes.map((code, index) => ({
          OrganisationCode: code,
          Rank: index + 1,
          RankTotal: index + 1,
          RankEqual: 'N',
          MedalNumbers: [
            {
              Type: 'TOT',
              Gold: 0,
              Silver: 0,
              Bronze: 0,
              Total: 0
            }
          ]
        }));

        setFallbackRows(rows);
      } catch (fallbackError) {
        if (!cancelled) {
          console.error('Error building medals fallback rows:', fallbackError);
          setFallbackRows([]);
        }
      } finally {
        if (!cancelled) {
          setIsFallbackLoading(false);
        }
      }
    };

    buildFallbackRows();

    return () => {
      cancelled = true;
    };
  }, [shouldUseFallback]);

  const sourceRows = shouldUseFallback ? fallbackRows : medalCountryRows;

  const medals = useMemo(() => {
    if (!sourceRows.length) return [];
    const effectiveSortBy = sortBy || 'gold';

    return [...sourceRows].sort((a, b) => {
      const aM = a.MedalNumbers?.find(m => m.Type === 'TOT');
      const bM = b.MedalNumbers?.find(m => m.Type === 'TOT');
      const aCountry = getCountryName(a.OrganisationCode);
      const bCountry = getCountryName(b.OrganisationCode);
      
      // Use API rank fields for rank, gold, and total sorting
      if (effectiveSortBy === 'rank') return a.Rank - b.Rank;
      if (effectiveSortBy === 'gold') return a.Rank - b.Rank;
      if (effectiveSortBy === 'total') return a.RankTotal - b.RankTotal;
      if (effectiveSortBy === 'country') return aCountry.localeCompare(bCountry);
      
      // Manual sort for silver and bronze (API doesn't provide these ranks)
      if (effectiveSortBy === 'silver') {
        if ((bM?.Silver || 0) !== (aM?.Silver || 0)) return (bM?.Silver || 0) - (aM?.Silver || 0);
        if ((bM?.Gold || 0) !== (aM?.Gold || 0)) return (bM?.Gold || 0) - (aM?.Gold || 0);
        return (bM?.Bronze || 0) - (aM?.Bronze || 0);
      }
      // bronze
      if ((bM?.Bronze || 0) !== (aM?.Bronze || 0)) return (bM?.Bronze || 0) - (aM?.Bronze || 0);
      if ((bM?.Gold || 0) !== (aM?.Gold || 0)) return (bM?.Gold || 0) - (aM?.Gold || 0);
      return (bM?.Silver || 0) - (aM?.Silver || 0);
    });
  }, [sourceRows, sortBy]);

  useEffect(() => {
    const tableBodyEl = tableBodyRef.current;
    if (!tableBodyEl) return undefined;

    const updateScrollState = () => {
      const maxScrollTop = tableBodyEl.scrollHeight - tableBodyEl.clientHeight;
      if (maxScrollTop <= 1) {
        setHasOverflow(false);
        setCanScrollUp(false);
        setCanScrollDown(false);
        return;
      }

      setHasOverflow(true);
      setCanScrollUp(tableBodyEl.scrollTop > 1);
      setCanScrollDown(tableBodyEl.scrollTop < maxScrollTop - 1);
    };

    const rafId = window.requestAnimationFrame(updateScrollState);
    const settleTimeoutId = window.setTimeout(updateScrollState, 160);
    let resizeObserver;

    if (typeof window.ResizeObserver !== 'undefined') {
      resizeObserver = new window.ResizeObserver(updateScrollState);
      resizeObserver.observe(tableBodyEl);
    }

    const stopTouchPropagation = (event) => {
      event.stopPropagation();
    };

    tableBodyEl.addEventListener('scroll', updateScrollState, { passive: true });
    tableBodyEl.addEventListener('touchstart', stopTouchPropagation, { passive: true });
    tableBodyEl.addEventListener('touchmove', stopTouchPropagation, { passive: true });
    window.addEventListener('resize', updateScrollState);
    window.addEventListener('orientationchange', updateScrollState);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(settleTimeoutId);
      tableBodyEl.removeEventListener('scroll', updateScrollState);
      tableBodyEl.removeEventListener('touchstart', stopTouchPropagation);
      tableBodyEl.removeEventListener('touchmove', stopTouchPropagation);
      window.removeEventListener('resize', updateScrollState);
      window.removeEventListener('orientationchange', updateScrollState);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [medals.length, showAll, sortBy, isActive]);

  const nudgeTable = (direction) => {
    const tableBodyEl = tableBodyRef.current;
    if (!tableBodyEl) return;

    const step = Math.max(140, Math.round(tableBodyEl.clientHeight * 0.55));
    tableBodyEl.scrollBy({
      top: direction * step,
      behavior: 'smooth'
    });
  };

  const effectiveShowAll = noHeightLimit || showAll;
  const hasScrollableRows = hasOverflow;
  const hasShowAllControl = !noHeightLimit && medals.length > VISIBLE_COUNT;

  const getFlagIconSrc = (countryCode) => {
    if (!countryCode) return DEFAULT_FLAG_ICON;
    return FLAG_ICONS_BY_CODE[countryCode.toUpperCase()] || DEFAULT_FLAG_ICON;
  };

  if (error) {
    return (
      <div className="tab-content error-state">
        <p className="error-message">Failed to load medal tally</p>
        <button className="retry-button" onClick={refetch}>Retry</button>
      </div>
    );
  }

  if (isLoading || !data || (shouldUseFallback && isFallbackLoading && fallbackRows.length === 0)) {
    return (
      <div className="tab-content loading-state">
        <SkeletonLoader count={20} type="row" />
      </div>
    );
  }

  return (
    <div className="tab-content medal-tally">
      <div className="medals-table">
        <div className="table-header">
          <div className={`col-rank col-rank--sortable ${sortBy === 'rank' ? 'col-sorted' : ''}`} onClick={() => setSortBy('rank')}>
            <span className="sort-pill">#</span>
          </div>
          <div className={`col-country col-country--sortable ${sortBy === 'country' ? 'col-sorted' : ''}`} onClick={() => setSortBy('country')}>
            <span className="sort-pill">
              <span className="sort-label sort-label--desktop">Country</span>
              <span className="sort-label sort-label--mobile">Country</span>
            </span>
          </div>
          <div className={`col-gold ${sortBy === 'gold' ? 'col-sorted' : ''}`} onClick={() => setSortBy('gold')}>
            <span className="medal-disc medal-disc--gold"></span>
            <div className="medal-label">
              <span className="sort-pill">Gold</span>
            </div>
          </div>
          <div className={`col-silver ${sortBy === 'silver' ? 'col-sorted' : ''}`} onClick={() => setSortBy('silver')}>
            <span className="medal-disc medal-disc--silver"></span>
            <div className="medal-label">
              <span className="sort-pill">Silver</span>
            </div>
          </div>
          <div className={`col-bronze ${sortBy === 'bronze' ? 'col-sorted' : ''}`} onClick={() => setSortBy('bronze')}>
            <span className="medal-disc medal-disc--bronze"></span>
            <div className="medal-label">
              <span className="sort-pill">Bronze</span>
            </div>
          </div>
          <div className={`col-total ${sortBy === 'total' ? 'col-sorted' : ''}`} onClick={() => setSortBy('total')}>
            <span className="medal-disc-stack">
              <span className="medal-disc medal-disc--gold"></span>
              <span className="medal-disc medal-disc--silver"></span>
              <span className="medal-disc medal-disc--bronze"></span>
            </span>
            <div className="medal-label">
              <span className="sort-pill">Total</span>
            </div>
          </div>
        </div>
        <div className="table-body" ref={tableBodyRef}>
          {(effectiveShowAll ? medals : medals.slice(0, VISIBLE_COUNT)).map((country, idx) => {
            const medalData = country.MedalNumbers?.find(m => m.Type === 'TOT');
            const countryCode = country.OrganisationCode;
            const countryName = getCountryName(countryCode);
            const rank = country.Rank;
            const isEqual = country.RankEqual === 'Y';
            return (
              <div key={`${country.OrganisationCode}-${idx}`} className="table-row">
                <div className="col-rank">{isEqual ? `=${rank}` : rank}</div>
                <div className="col-country" title={countryName}>
                  <img
                    className="country-flag"
                    src={getFlagIconSrc(countryCode)}
                    alt=""
                    loading="lazy"
                    decoding="async"
                  />
                  <span className="country-name country-name--desktop">{countryName}</span>
                  <span className="country-name country-name--mobile">{countryCode}</span>
                </div>
                <div className="col-gold">{medalData?.Gold || 0}</div>
                <div className="col-silver">{medalData?.Silver || 0}</div>
                <div className="col-bronze">{medalData?.Bronze || 0}</div>
                <div className="col-total">{medalData?.Total || 0}</div>
              </div>
            );
          })}
        </div>
        {(hasScrollableRows || hasShowAllControl) && (
          <div className={`schedule-scroll-bar ${canScrollDown ? 'schedule-scroll-bar--has-below' : ''}`.trim()}>
            <div className="schedule-scroll-bar__main">
              {hasShowAllControl && (
                <button className="schedule-scroll-bar__main-btn" onClick={() => setShowAll(prev => !prev)}>
                  {showAll ? 'Show less' : `Show all ${medals.length} countries`}
                </button>
              )}
            </div>
            {hasScrollableRows && (
              <div className="schedule-scroll-bar__controls">
                <button
                  type="button"
                  className="schedule-scroll-bar__btn"
                  onClick={() => nudgeTable(-1)}
                  disabled={!canScrollUp}
                  aria-label="Scroll medal table up"
                >
                  <svg className="schedule-scroll-bar__icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                    <path d="M8 3L14 11H2Z" />
                  </svg>
                </button>
                <button
                  type="button"
                  className="schedule-scroll-bar__btn"
                  onClick={() => nudgeTable(1)}
                  disabled={!canScrollDown}
                  aria-label="Scroll medal table down"
                >
                  <svg className="schedule-scroll-bar__icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                    <path d="M8 13L2 5h12Z" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
